import { SECTION_STYLES } from "@components/jobs/jobs-list/jobs-layout/components/Summary/JobScopeOfWorkView";
import { colors } from "@components/ui/colors";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { CARD_STYLES } from "./ScopeOfWorkTypeOfService";

const getAreaQuantity = (area: any): number => {
  if (area?.quantity != null) {
    const quantity = Number(area.quantity);

    return Number.isFinite(quantity) && quantity > 0 ? quantity : 0;
  }

  const squareFootages = Array.isArray(area?.squareFootages) ? area.squareFootages : [];

  return squareFootages.reduce((total: number, item: any) => total + (Number(item?.quantity) || 0), 0);
};

export const ScopeOfWorkAreas = ({ areas = [] }) => {
  const activeAreas = areas.filter((area: any) => getAreaQuantity(area) > 0);

  if (!activeAreas.length) return null;

  return (
    <Box sx={SECTION_STYLES.root}>
      <SectionHeader title="Areas included" count={activeAreas.length} icon={<GridViewRoundedIcon sx={{ fontSize: 20 }} />} />

      <Stack spacing={1}>
        {activeAreas.map((area: any, index: number) => {
          const areaUid = area?.uid ?? area?.id ?? `area-${index}`;

          return <AreaCollapseItem key={areaUid} area={area} />;
        })}
      </Stack>
    </Box>
  );
};

function AreaCollapseItem({ area }) {
  const [open, setOpen] = useState(false);

  const tasks = area.tasks ?? [];
  const notesCount = tasks.filter((task) => Boolean(task.note?.trim())).length;
  const hasNotes = notesCount > 0;
  const areaName = area.name ?? area.area?.name ?? area.typeOfArea?.name ?? area.areaTemplate?.name ?? "Unnamed area";
  const quantity = getAreaQuantity(area);

  return (
    <Box sx={SECTION_STYLES.listItem}>
      <Box onClick={() => setOpen((prev) => !prev)} sx={SECTION_STYLES.listHeader}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
          <AreaIcon area={area} areaName={areaName} />

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: "0.9rem",
                fontWeight: 900,
                color: "text.primary",
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                maxWidth: "100%",
              }}
              title={areaName}
            >
              {areaName}
            </Typography>

            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                mt: 0.65,
                flexWrap: "wrap",
                rowGap: 0.65,
              }}
            >
              <InfoPill label={`Qty: ${quantity}`} />
              <InfoPill label={`${tasks.length} tasks`} />

              <InfoPill
                tone={hasNotes ? "info" : "muted"}
                label={hasNotes ? `${notesCount} notes` : "No notes"}
                icon={<NotesRoundedIcon sx={{ fontSize: 13 }} />}
              />
            </Stack>
          </Box>
        </Stack>

        <IconButton
          size="small"
          sx={{
            width: 30,
            height: 30,
            flexShrink: 0,
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "0.2s ease",
          }}
        >
          <ExpandMoreRoundedIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box
          sx={{
            mt: 1.25,
            pt: 1.25,
            borderTop: "1px solid rgba(var(--fx-surface-textrgb, 15, 23, 42), 0.06)",
          }}
        >
          {area.imageUrl ? (
            <Box
              sx={{
                width: "100%",
                height: 120,
                borderRadius: 2.5,
                overflow: "hidden",
                bgcolor: "var(--fx-surface-sunken, #F8FAFC)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 1.25,
              }}
            >
              <Box
                component="img"
                src={area.imageUrl}
                alt={areaName}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Box>
          ) : null}

          <Box
            sx={{
              borderColor: colors.orange[600],
              pl: 1.5,
              py: 0.75,
              borderRadius: "0 10px 10px 0",
            }}
          >
            <Typography sx={SECTION_STYLES.subLabel}>Areas & Tasks</Typography>

            {tasks.length > 0 ? (
              <Stack spacing={0.65}>
                {tasks.map((task) => {
                  const hasNote = Boolean(task.note?.trim());

                  return (
                    <Box key={task.uid}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                        <Typography
                          sx={{
                            fontSize: "0.8rem",
                            fontWeight: 700,
                            color: "text.primary",
                            lineHeight: 1.35,
                          }}
                        >
                          • {task.name}
                        </Typography>

                        <Box sx={hasNote ? SECTION_STYLES.noteBadge : null}>{hasNote ? "Has note" : null}</Box>
                      </Stack>

                      {hasNote ? (
                        <Typography
                          sx={{
                            ml: 1.4,
                            mt: 0.25,
                            fontSize: "0.74rem",
                            color: "primary.main",
                            fontStyle: "italic",
                            lineHeight: 1.4,
                          }}
                        >
                          {task.note}
                        </Typography>
                      ) : null}
                    </Box>
                  );
                })}
              </Stack>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "text.secondary",
                  fontStyle: "italic",
                }}
              >
                No tasks assigned to this area.
              </Typography>
            )}
          </Box>
        </Box>
      </Collapse>
    </Box>
  );
}
function AreaIcon({ area, areaName }: { area: any; areaName: string }) {
  const imageUrl = area.imageUrl ?? area.area?.imageUrl ?? area.typeOfArea?.imageUrl ?? area.areaTemplate?.imageUrl ?? null;

  return (
    <Box sx={SECTION_STYLES.areaImageBox}>
      {imageUrl ? (
        <Box
          component="img"
          src={imageUrl}
          alt={areaName}
          sx={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
          }}
        />
      ) : (
        <ChecklistRoundedIcon sx={{ fontSize: 17 }} />
      )}
    </Box>
  );
}

function SectionHeader({ title, count, icon }: { title: string; count: number; icon: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ mb: 1.5 }}>
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Box sx={SECTION_STYLES.iconBox}>{icon}</Box>

        <Box>
          <Typography sx={CARD_STYLES.sectionLabel}>{title}</Typography>
        </Box>
      </Stack>

      <Box sx={SECTION_STYLES.pill}>{count} selected</Box>
    </Stack>
  );
}

function InfoPill({ label, tone = "default", icon }: { label: string; tone?: "default" | "info" | "muted"; icon?: React.ReactNode }) {
  const sxByTone = {
    default: SECTION_STYLES.infoPill,
    info: SECTION_STYLES.infoPillInfo,
    muted: SECTION_STYLES.infoPillMuted,
  };

  return (
    <Box sx={sxByTone[tone]}>
      {icon}
      {label}
    </Box>
  );
}
